import { useEffect, type ReactElement } from 'react';
import { useExchange } from '../api/public-queries';
import { StatusState } from '../ui/StatusState';
import { ApiError } from '../api/errors';
import type { SupabaseAuth } from '../auth/supabase';

export const CALLBACK_MESSAGES = {
  noSession: 'That sign-in link has expired. Send a new one.',
  /** The server's grace-period refusal (DELETED_ACCOUNT_MESSAGE in auth-service.ts). */
  deleted: 'This account is being deleted. Email support to restore it.',
  401: "We couldn't verify that sign-in. Try again.",
  403: 'This account has been disabled.',
  503: 'Sign-in is temporarily unavailable, try again.'
} as const;

function messageForStatus(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return CALLBACK_MESSAGES[401];
    if (error.status === 403) {
      return error.message === CALLBACK_MESSAGES.deleted ? CALLBACK_MESSAGES.deleted : CALLBACK_MESSAGES[403];
    }
    if (error.status === 503) return CALLBACK_MESSAGES[503];
  }
  return CALLBACK_MESSAGES[503];
}

/** Supabase reports a refused sign-in in the callback URL, in the query (PKCE) or the hash (implicit). */
function supabaseErrorCode(location: { search: string; hash: string }): string | null {
  for (const part of [location.search, location.hash]) {
    const code = new URLSearchParams(part.replace(/^[?#]/, '')).get('error_code');
    if (code) return code;
  }
  return null;
}

export function AuthCallbackRoute(props: {
  auth: SupabaseAuth;
  onSignedIn(): void;
  onFailed(message: string): void;
  /** Defaults to the window's location; tests pass their own. */
  location?: { search: string; hash: string };
}): ReactElement {
  const exchange = useExchange();
  const exchangeAsync = exchange.mutateAsync;
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // A deleted account is banned at Supabase, so its sign-in never yields a session.
      if (supabaseErrorCode(props.location ?? window.location) === 'user_banned') {
        return props.onFailed(CALLBACK_MESSAGES.deleted);
      }
      let token: string | null = null;
      try {
        token = await props.auth.getAccessToken();
      } catch {
        token = null;
      }
      if (cancelled) return;
      if (!token) return props.onFailed(CALLBACK_MESSAGES.noSession);
      try {
        await exchangeAsync(token);
        if (!cancelled) props.onSignedIn();
      } catch (e) {
        if (!cancelled) props.onFailed(messageForStatus(e));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <StatusState kind="loading" title="Signing you in…" page />;
}
